import React, { useState, useEffect, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileUploadDropzone from './FileUploadDropzone';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import StarIcon from '@mui/icons-material/Star';
import {
    Box, Typography, Button, Modal, TextField, MenuItem, IconButton, Grid,
    Stepper, Step, StepLabel, CircularProgress, Alert, Avatar, Divider,
    InputAdornment, FormControl, InputLabel, Select, FormControlLabel, Switch,
    Card, CardMedia, Chip
} from '@mui/material';
import { getCurrencySymbol } from '../utils/currency';

// ─── Constants ──────────────────────────────────────────────────────────────
const HOTEL_TYPES = [
    'Hotel',
    'Resort',
    'Boutique',
    'Serviced Apartment',
    'Hostel',
    'Guesthouse'
];
const CURRENCIES = ['INR', 'USD', 'AED', 'EUR', 'GBP', 'SAR', 'QAR', 'BHD', 'OMR', 'KWD'];
const TIMEZONES = [
    'Asia/Kolkata', 'Asia/Dubai', 'UTC', 'America/New_York', 'America/Chicago',
    'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Tokyo',
    'Asia/Singapore', 'Australia/Sydney', 'Pacific/Auckland'
];
const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'ar', label: 'Arabic' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'es', label: 'Spanish' },
    { code: 'hi', label: 'Hindi' },
    { code: 'zh', label: 'Chinese' },
    { code: 'ja', label: 'Japanese' },
];
const RATINGS = [1, 2, 3, 4, 5];

const STEPS = ['General Details', 'Location & Address', 'Contact & Media', 'Localization', 'Tenant Metadata'];

const DEFAULT_FORM = {
    name: '', hotelCode: '', hotelType: '', rating: '', tagline: '',
    location: '', address: '', city: '', state: '', country: '', postalCode: '',
    latitude: '', longitude: '',
    phone: '', email: '', website: '', adminEmail: '', logoUrl: '',
    images: [],
    primaryColor: '#4f46e5',
    checkInTime: '14:00', checkOutTime: '11:00',
    currency: 'INR', timezone: 'Asia/Kolkata', language: 'en',
    tenantId: '',
    trn: '',
    isSpecifiedPremises: false,
};

export default function HotelFormModal({ open, onClose, onSave, editingHotel = null }) {
    const [formData, setFormData] = useState(DEFAULT_FORM);
    const [errors, setErrors] = useState({});
    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    // ─── Populate form on open ───────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            if (editingHotel) {
                const initialImages = Array.isArray(editingHotel.images)
                    ? editingHotel.images
                    : (Array.isArray(editingHotel.photos) ? editingHotel.photos : []);

                setFormData({
                    ...DEFAULT_FORM,
                    ...editingHotel,
                    images: initialImages,
                    rating: editingHotel.rating || '',
                    latitude: editingHotel.latitude ?? '',
                    longitude: editingHotel.longitude ?? '',
                    tenantId: editingHotel.tenantId || '',
                    trn: editingHotel.trn || editingHotel.trnNumber || editingHotel.taxId || '',
                    isSpecifiedPremises: Boolean(editingHotel.isSpecifiedPremises)
                });
                setLogoPreview(editingHotel.logoUrl || null);
            } else {
                setFormData(DEFAULT_FORM);
                setLogoPreview(null);
            }
            setErrors({});
            setActiveStep(0);
            setSubmitError(null);
            setIsSubmitting(false);
        }
    }, [open, editingHotel]);

    // ─── Handlers ────────────────────────────────────────────────────────────
    const handleChange = useCallback((e) => {
        let { name, value } = e.target;
        if (name === 'phone') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
        if (submitError) setSubmitError(null);
    }, [errors, submitError]);

    const removeLogo = useCallback(() => {
        setLogoPreview(null);
        setFormData(prev => ({ ...prev, logoUrl: '' }));
    }, []);

    const removeImage = useCallback((indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            images: (prev.images || []).filter((_, idx) => idx !== indexToRemove)
        }));
    }, []);


    // ─── Validation per step ─────────────────────────────────────────────────
    const validateStep = useCallback((step) => {
        const newErrors = {};

        if (step === 0) {
            if (!formData.name?.trim()) {
                newErrors.name = 'Hotel name is required';
            } else if (formData.name.length > 50) {
                newErrors.name = 'Hotel name cannot exceed 50 characters';
            }
            if (formData.tagline?.trim() && formData.tagline.trim().length > 100) {
                newErrors.tagline = 'Tagline cannot exceed 100 characters';
            }
            if (!formData.hotelCode?.trim()) {
                newErrors.hotelCode = 'Hotel code is required';
            } else if (!/^[a-zA-Z0-9-]+$/.test(formData.hotelCode.trim())) {
                newErrors.hotelCode = 'Hotel code must contain only alphanumeric characters and hyphens (e.g. HTL-001)';
            }
            if (!formData.hotelType) newErrors.hotelType = 'Hotel type is required';
            if (!formData.rating) newErrors.rating = 'Rating is required';
            if (!formData.primaryColor?.trim()) {
                newErrors.primaryColor = 'Primary color is required';
            } else if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(formData.primaryColor.trim())) {
                newErrors.primaryColor = 'Invalid hex color format (e.g. #4f46e5)';
            }
        }

        if (step === 1) {
            if (!formData.location?.trim()) newErrors.location = 'Location is required';
            if (!formData.address?.trim()) newErrors.address = 'Address is required';
            if (!formData.city?.trim()) newErrors.city = 'City is required';
            if (!formData.state?.trim()) newErrors.state = 'State is required';
            if (!formData.country?.trim()) newErrors.country = 'Country is required';
            if (!formData.postalCode?.trim()) {
                newErrors.postalCode = 'Postal code is required';
            } else if (!/^[a-zA-Z0-9\s-]{3,10}$/.test(formData.postalCode.trim())) {
                newErrors.postalCode = 'Invalid postal code format (3-10 alphanumeric characters)';
            }
            if (formData.latitude !== '' && formData.latitude !== null && formData.latitude !== undefined) {
                const lat = Number(formData.latitude);
                if (isNaN(lat)) {
                    newErrors.latitude = 'Must be a valid number';
                } else if (lat < -90 || lat > 90) {
                    newErrors.latitude = 'Latitude must be between -90 and 90';
                }
            }
            if (formData.longitude !== '' && formData.longitude !== null && formData.longitude !== undefined) {
                const lon = Number(formData.longitude);
                if (isNaN(lon)) {
                    newErrors.longitude = 'Must be a valid number';
                } else if (lon < -180 || lon > 180) {
                    newErrors.longitude = 'Longitude must be between -180 and 180';
                }
            }
        }

        if (step === 2) {
            if (!formData.phone?.trim()) newErrors.phone = 'Phone number is required';
            else if (formData.phone.length !== 10) newErrors.phone = 'Phone number must be exactly 10 digits';
            if (!formData.email?.trim()) newErrors.email = 'Hotel email is required';
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
            if (formData.adminEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
                newErrors.adminEmail = 'Invalid email format';
            }
            if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
                newErrors.website = 'Must start with http:// or https://';
            }
        }

        if (step === 3) {
            if (!formData.checkInTime) newErrors.checkInTime = 'Check-in time required';
            if (!formData.checkOutTime) newErrors.checkOutTime = 'Check-out time required';
        }

        if (step === 4) {
            if (!formData.tenantId) newErrors.tenantId = 'Tenant ID is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    // ─── Navigation ──────────────────────────────────────────────────────────
    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    const handleStepClick = (step) => {
        // Only allow going to previously visited steps
        if (step < activeStep) {
            setActiveStep(step);
        }
    };

    // ─── Submit ──────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validateStep(activeStep)) return;

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const payload = {
                hotelCode: formData.hotelCode.toUpperCase().replace(/<\/?[^>]+(>|$)/g, ""),
                rating: Number(formData.rating),
                tenantId: String(formData.tenantId).replace(/<\/?[^>]+(>|$)/g, "")
            };

            // Only add fields from DEFAULT_FORM that have valid values
            Object.keys(DEFAULT_FORM).forEach(key => {
                if (key !== 'hotelCode' && key !== 'rating' && key !== 'tenantId') {
                    let val = formData[key];
                    if (key === 'images') {
                        payload.images = Array.isArray(val) ? val : [];
                        return;
                    }
                    if (typeof val === 'string') {
                        val = val.trim().replace(/<\/?[^>]+(>|$)/g, "");
                    }
                    
                    if (val !== null && val !== undefined && val !== '' && val !== 'null') {
                        payload[key] = val;
                    }
                }
            });

            // Handle latitude and longitude correctly
            if (formData.latitude !== '' && formData.latitude !== null && formData.latitude !== undefined) {
                payload.latitude = parseFloat(formData.latitude);
            } else {
                delete payload.latitude;
            }
            if (formData.longitude !== '' && formData.longitude !== null && formData.longitude !== undefined) {
                payload.longitude = parseFloat(formData.longitude);
            } else {
                delete payload.longitude;
            }

            // Include ID if editing
            if (editingHotel && editingHotel.id) {
                payload.id = editingHotel.id;
            }

            await onSave(payload);
            onClose();
        } catch (error) {
            const msg = error?.response?.data?.message || error?.message || 'Failed to create hotel property';
            setSubmitError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleModalClose = (event, reason) => {
        if (reason === 'backdropClick' || isSubmitting) return;
        onClose();
    };

    // ─── Step Content Renderers ──────────────────────────────────────────────
    const renderStep0 = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    fullWidth label="Hotel Name" name="name" required
                    value={formData.name} onChange={handleChange}
                    error={!!errors.name} helperText={errors.name}
                    disabled={isSubmitting} size="small"
                    placeholder="e.g. PMSGO Resort"
                    inputProps={{ maxLength: 50 }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Hotel Code" name="hotelCode" required
                    value={formData.hotelCode} onChange={handleChange}
                    error={!!errors.hotelCode} helperText={errors.hotelCode || 'Uppercase shorthand, e.g. HTL-001'}
                    disabled={isSubmitting} size="small"
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    placeholder="HTL-001"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" error={!!errors.hotelType} required>
                    <InputLabel>Hotel Type</InputLabel>
                    <Select
                        name="hotelType" label="Hotel Type"
                        value={formData.hotelType} onChange={handleChange}
                        disabled={isSubmitting}
                    >
                        {HOTEL_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                    {errors.hotelType && <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>{errors.hotelType}</Typography>}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" error={!!errors.rating} required>
                    <InputLabel>Star Rating</InputLabel>
                    <Select
                        name="rating" label="Star Rating"
                        value={formData.rating} onChange={handleChange}
                        disabled={isSubmitting}
                    >
                        {RATINGS.map(r => (
                            <MenuItem key={r} value={r}>
                                {'⭐'.repeat(r)} ({r})
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.rating && <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>{errors.rating}</Typography>}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Primary Color" name="primaryColor" required
                    value={formData.primaryColor} onChange={handleChange}
                    error={!!errors.primaryColor} helperText={errors.primaryColor}
                    disabled={isSubmitting} size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Box sx={{ 
                                    width: 20, 
                                    height: 20, 
                                    borderRadius: '50%', 
                                    bgcolor: /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(formData.primaryColor) ? formData.primaryColor : '#ccc', 
                                    border: '1px solid #ccc' 
                                }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth label="Tagline" name="tagline"
                    value={formData.tagline} onChange={handleChange}
                    error={!!errors.tagline} helperText={errors.tagline}
                    disabled={isSubmitting} size="small"
                    placeholder="e.g. Experience luxury like never before"
                    multiline rows={2}
                    inputProps={{ maxLength: 100 }}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.isSpecifiedPremises}
                            onChange={(e) => setFormData(prev => ({ ...prev, isSpecifiedPremises: e.target.checked }))}
                            color="primary"
                            disabled={isSubmitting}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2" fontWeight="bold">Specified Premises (Luxurious Property)</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formData.currency === 'AED'
                                    ? "Specified premises classification (hotel property rating / luxury tier)."
                                    : `Enable if any room tariff exceeds ${getCurrencySymbol(formData.currency)}7,500/night (Applies 18% F&B GST rate).`}
                            </Typography>
                        </Box>
                    }
                />
            </Grid>
        </Grid>
    );

    const renderStep1 = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    fullWidth label="Location" name="location" required
                    value={formData.location} onChange={handleChange}
                    error={!!errors.location} helperText={errors.location}
                    disabled={isSubmitting} size="small"
                    placeholder="e.g. Dubai"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth label="Address" name="address" required
                    value={formData.address} onChange={handleChange}
                    error={!!errors.address} helperText={errors.address}
                    disabled={isSubmitting} size="small"
                    placeholder="e.g. Downtown Dubai, UAE"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="City" name="city" required
                    value={formData.city} onChange={handleChange}
                    error={!!errors.city} helperText={errors.city}
                    disabled={isSubmitting} size="small"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="State / Province" name="state" required
                    value={formData.state} onChange={handleChange}
                    error={!!errors.state} helperText={errors.state}
                    disabled={isSubmitting} size="small"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Country" name="country" required
                    value={formData.country} onChange={handleChange}
                    error={!!errors.country} helperText={errors.country}
                    disabled={isSubmitting} size="small"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Postal Code" name="postalCode" required
                    value={formData.postalCode} onChange={handleChange}
                    error={!!errors.postalCode} helperText={errors.postalCode}
                    disabled={isSubmitting} size="small"
                />
            </Grid>
            <Divider sx={{ width: '100%', my: 1 }}>
                <Typography variant="caption" color="text.secondary">Geographic Coordinates (optional)</Typography>
            </Divider>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Latitude" name="latitude" type="number"
                    value={formData.latitude} onChange={handleChange}
                    error={!!errors.latitude} helperText={errors.latitude || 'e.g. 25.2048'}
                    disabled={isSubmitting} size="small"
                    inputProps={{ step: 'any' }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Longitude" name="longitude" type="number"
                    value={formData.longitude} onChange={handleChange}
                    error={!!errors.longitude} helperText={errors.longitude || 'e.g. 55.2708'}
                    disabled={isSubmitting} size="small"
                    inputProps={{ step: 'any' }}
                />
            </Grid>
        </Grid>
    );

    const renderStep2 = () => (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Phone" name="phone" required
                    value={formData.phone} onChange={handleChange}
                    error={!!errors.phone} helperText={errors.phone}
                    disabled={isSubmitting} size="small"
                    placeholder="+971 4 123 4567"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Hotel Email" name="email" required
                    value={formData.email} onChange={handleChange}
                    error={!!errors.email} helperText={errors.email}
                    disabled={isSubmitting} size="small"
                    placeholder="resort@pmsgo.com"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Website" name="website"
                    value={formData.website} onChange={handleChange}
                    error={!!errors.website} helperText={errors.website}
                    disabled={isSubmitting} size="small"
                    placeholder="https://pmsgoresort.com"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Admin Invitation Email" name="adminEmail"
                    value={formData.adminEmail} onChange={handleChange}
                    error={!!errors.adminEmail} helperText={errors.adminEmail || 'An invite will be sent to this email'}
                    disabled={isSubmitting} size="small"
                    placeholder="admin@example.com"
                />
            </Grid>
            <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Hotel Logo</Typography>
                </Divider>
            </Grid>
            <Grid item xs={12}>
                {logoPreview ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ position: 'relative' }}>
                            <Avatar
                                src={logoPreview}
                                variant="rounded"
                                sx={{ width: 80, height: 80, border: '2px solid', borderColor: 'divider' }}
                            />
                            <IconButton
                                size="small"
                                onClick={removeLogo}
                                disabled={isSubmitting}
                                sx={{
                                    position: 'absolute', top: -8, right: -8,
                                    bgcolor: 'error.main', color: 'white',
                                    '&:hover': { bgcolor: 'error.dark' },
                                    width: 22, height: 22
                                }}
                            >
                                <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Box>
                        <Box>
                            <Button
                                variant="outlined" size="small" color="error"
                                disabled={isSubmitting}
                                onClick={removeLogo}
                            >
                                Remove Logo
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <FileUploadDropzone 
                        accept="image/png,image/jpeg" 
                        maxSizeMB={2}
                        onUploadSuccess={(url) => {
                            setLogoPreview(url);
                            setFormData(prev => ({ ...prev, logoUrl: url }));
                        }} 
                    />
                )}
                {errors.logoUrl && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                        {errors.logoUrl}
                    </Typography>
                )}
            </Grid>
            <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Hotel Property Images (Gallery)</Typography>
                </Divider>
            </Grid>
            <Grid item xs={12}>
                <FileUploadDropzone 
                    accept="image/png,image/jpeg,image/webp,image/jpg" 
                    maxSizeMB={5}
                    multiple={true}
                    onMultipleUploadSuccess={(urls) => {
                        setFormData(prev => ({
                            ...prev,
                            images: [...(prev.images || []), ...urls]
                        }));
                    }} 
                />
            </Grid>
            {formData.images && formData.images.length > 0 && (
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                            Uploaded Gallery Images ({formData.images.length})
                        </Typography>
                        <Button 
                            size="small" 
                            color="error" 
                            onClick={() => setFormData(prev => ({ ...prev, images: [] }))}
                            disabled={isSubmitting}
                        >
                            Clear All Photos
                        </Button>
                    </Box>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                        gap: 1.5,
                        mt: 0.5
                    }}>
                        {formData.images.map((imgUrl, idx) => (
                            <Card
                                key={idx}
                                elevation={0}
                                sx={{
                                    position: 'relative',
                                    borderRadius: 2,
                                    border: idx === 0 ? '2px solid #4f46e5' : '1px solid #e2e8f0',
                                    height: 90,
                                    overflow: 'hidden'
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    image={imgUrl}
                                    alt={`Hotel photo ${idx + 1}`}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                {idx === 0 && (
                                    <Chip
                                        icon={<StarIcon sx={{ fontSize: '11px !important', color: '#F59E0B !important' }} />}
                                        label="Cover"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            left: 4,
                                            height: 18,
                                            fontSize: '0.65rem',
                                            bgcolor: 'rgba(0,0,0,0.75)',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            '& .MuiChip-label': { px: 0.5 }
                                        }}
                                    />
                                )}
                                <IconButton
                                    size="small"
                                    onClick={() => removeImage(idx)}
                                    disabled={isSubmitting}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        bgcolor: 'rgba(239, 68, 68, 0.9)',
                                        color: 'white',
                                        p: 0.4,
                                        '&:hover': { bgcolor: 'error.dark' }
                                    }}
                                >
                                    <DeleteIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Card>
                        ))}
                    </Box>
                </Grid>
            )}
        </Grid>
    );

    const renderStep3 = () => (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                    <InputLabel>Currency</InputLabel>
                    <Select
                        name="currency" label="Currency"
                        value={formData.currency} onChange={handleChange}
                        disabled={isSubmitting}
                    >
                        {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                    <InputLabel>Timezone</InputLabel>
                    <Select
                        name="timezone" label="Timezone"
                        value={formData.timezone} onChange={handleChange}
                        disabled={isSubmitting}
                    >
                        {TIMEZONES.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                    <InputLabel>Language</InputLabel>
                    <Select
                        name="language" label="Language"
                        value={formData.language} onChange={handleChange}
                        disabled={isSubmitting}
                    >
                        {LANGUAGES.map(l => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label={formData.currency === 'AED' || (formData.country || '').toLowerCase().includes('uae') ? "TRN (Tax Registration Number)" : "GSTIN / Tax ID"}
                    name="trn"
                    value={formData.trn || ''}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    size="small"
                    placeholder={formData.currency === 'AED' ? "e.g. 100234567800003" : "e.g. 27AAAAA0000A1Z5"}
                    helperText={formData.currency === 'AED' ? "15-digit UAE FTA Tax Registration Number" : "Applicable business tax registration number"}
                />
            </Grid>
            <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Check-in / Check-out Times</Typography>
                </Divider>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Check-in Time" name="checkInTime" type="time" required
                    value={formData.checkInTime} onChange={handleChange}
                    error={!!errors.checkInTime} helperText={errors.checkInTime || 'Format: HH:mm'}
                    disabled={isSubmitting} size="small"
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth label="Check-out Time" name="checkOutTime" type="time" required
                    value={formData.checkOutTime} onChange={handleChange}
                    error={!!errors.checkOutTime} helperText={errors.checkOutTime || 'Format: HH:mm'}
                    disabled={isSubmitting} size="small"
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
        </Grid>
    );

    const renderStep4 = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                    The Tenant ID maps this property to its isolated data silo in the system.
                </Alert>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth label="Tenant ID" name="tenantId" required
                    value={formData.tenantId} onChange={handleChange}
                    error={!!errors.tenantId} helperText={errors.tenantId || 'Numeric tenant identifier, e.g. 7'}
                    disabled={isSubmitting} size="small"
                    placeholder="7"
                />
            </Grid>
        </Grid>
    );

    const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4];
    const isLastStep = activeStep === STEPS.length - 1;

    return (
        <Modal open={open} onClose={handleModalClose} disableEscapeKeyDown>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: 640 }, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24,
                display: 'flex', flexDirection: 'column', maxHeight: '92vh',
            }}>
                {/* ── Header ─────────────────────────────────────── */}
                <Box sx={{ px: 3, pt: 3, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                            {editingHotel ? 'Edit Hotel Property' : 'Register New Hotel'}
                        </Typography>
                        <IconButton onClick={onClose} disabled={isSubmitting} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 0.5 }}>
                        {STEPS.map((label, index) => (
                            <Step key={label} completed={index < activeStep} sx={{ cursor: index < activeStep ? 'pointer' : 'default' }}>
                                <StepLabel
                                    onClick={() => handleStepClick(index)}
                                    sx={{
                                        '& .MuiStepLabel-label': { fontSize: '0.7rem' },
                                    }}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* ── Body ───────────────────────────────────────── */}
                <Box sx={{ px: 3, py: 2.5, overflowY: 'auto', flexGrow: 1 }}>
                    {submitError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
                            {submitError}
                        </Alert>
                    )}
                    {stepRenderers[activeStep]()}
                </Box>

                {/* ── Footer ─────────────────────────────────────── */}
                <Box sx={{
                    px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2,
                }}>
                    <Button
                        variant="outlined" onClick={handleBack}
                        startIcon={<ArrowBackIcon />}
                        disabled={activeStep === 0 || isSubmitting}
                    >
                        Back
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button variant="outlined" color="inherit" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        {isLastStep ? (
                            <Button
                                variant="contained" onClick={handleSubmit}
                                startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                                disabled={isSubmitting}
                                sx={{ minWidth: 140 }}
                            >
                                {isSubmitting ? 'Creating...' : (editingHotel ? 'Update Hotel' : 'Create Hotel')}
                            </Button>
                        ) : (
                            <Button
                                variant="contained" onClick={handleNext}
                                endIcon={<ArrowForwardIcon />}
                                disabled={isSubmitting}
                            >
                                Next
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}
